
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType
} from "vue";
import { useRouter } from "vue-router";
import Checkbox from "primevue/checkbox";
import { useAuth } from "../../modules/auth";
import {
  useRntrcTipos,
  SERVICOS_APOS_CONSULTA,
  servicoNoNomeDoDonoAtual,
  placaEmFrota,
  placasParaServico
} from "../../modules/business/rntrc/rntrcSolicitacaoBusiness";
import {
  RntrcSolicitacaoPayload,
  RntrcSolicitacaoVeiculoPayload,
  RntrcTipoServicoPayload,
  TipoServicoRntrc,
  formatCpfCnpj,
  formatPlaca,
  getServicoVeiculoLabel,
  getTipoServicoLabel
} from "../../modules/business/rntrc/rntrcTypes";

interface OpcaoServico {
  flag: TipoServicoRntrc;
  descricao: string;
  resumo: string;
  quantidade: number;
}

interface GrupoPlacas {
  chave: string;
  titulo: string;
  veiculos: Array<RntrcSolicitacaoVeiculoPayload>;
}

interface RntrcOutroServicoState {
  aberto: boolean;
  stateReady: boolean;
  stateId: string;
  tiposHabilitados: Array<RntrcTipoServicoPayload>;
  tipo: TipoServicoRntrc | null;
  selecionados: Array<number>;
}

const DICAS: Partial<Record<TipoServicoRntrc, string>> = {
  IMF:
    "Renavam e tipo vêm do extrato da ANTT. Na próxima tela, cadastre cada veículo com o CRLV (proprietário e arquivo).",
  IAR:
    "Renavam e tipo vêm do extrato da ANTT. Na próxima tela, cadastre cada veículo com o CRLV e informe o arrendatário.",
  EXC:
    "A exclusão sai no nome de quem tem a placa hoje, que paga o PIX. Não precisa de CRLV.",
  TRF:
    "A transferência sai no nome de quem tem a placa hoje; na próxima tela você informa o destino.",
  ALT:
    "A alteração sai no nome de quem tem a placa hoje; confira os dados na próxima tela."
};

/**
 * Consulta de veículo concluída: escolhe um serviço e as placas dele, e abre a nova solicitação
 * já preenchida com o que a consulta trouxe (dono atual, renavam e tipo do extrato).
 */
export default defineComponent({
  emits: ["close"],
  setup(props, { emit }) {
    const router = useRouter();
    const state = reactive<RntrcOutroServicoState>({
      aberto: true,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      tiposHabilitados: [],
      tipo: null,
      selecionados: []
    });

    const veiculos = computed(() => props.solicitacao?.veiculos ?? []);

    const opcoes = computed<Array<OpcaoServico>>(() => {
      const habilitados = state.tiposHabilitados.map((t) => t.flTipoServico);
      return SERVICOS_APOS_CONSULTA.filter(
        (t) => habilitados.indexOf(t) >= 0
      ).map((t) => {
        const quantidade = placasParaServico(props.solicitacao, t).length;
        return {
          flag: t,
          descricao: getTipoServicoLabel(t),
          quantidade,
          resumo: quantidade
            ? quantidade + " de " + veiculos.value.length + " placa(s)"
            : "Nenhuma placa está numa frota"
        };
      });
    });

    const grupos = computed<Array<GrupoPlacas>>(() => {
      const placas = placasParaServico(props.solicitacao, state.tipo);
      if (!servicoNoNomeDoDonoAtual(state.tipo)) {
        return [
          { chave: "todas", titulo: "Placas da consulta", veiculos: placas }
        ];
      }
      const porDono = [] as Array<GrupoPlacas>;
      placas.forEach((v) => {
        const chave = (v.cpfCnpjAtual ?? "").replace(/\D/g, "");
        let g = porDono.find((x) => x.chave === chave);
        if (!g) {
          g = {
            chave,
            titulo:
              (v.nomeTransportadorAtual ?? "") +
              " — " +
              formatCpfCnpj(v.cpfCnpjAtual),
            veiculos: []
          };
          porDono.push(g);
        }
        g.veiculos.push(v);
      });
      return porDono;
    });

    const escolhidos = computed(() =>
      veiculos.value.filter(
        (v) =>
          v.idRntrcSolicitacaoVeiculo &&
          state.selecionados.indexOf(v.idRntrcSolicitacaoVeiculo) >= 0
      )
    );

    const donosMisturados = computed(
      () =>
        servicoNoNomeDoDonoAtual(state.tipo) &&
        new Set(escolhidos.value.map((v) => v.cpfCnpjAtual)).size > 1
    );

    const podeProsseguir = computed(
      () =>
        !!state.tipo && escolhidos.value.length > 0 && !donosMisturados.value
    );

    const dica = computed(() => (state.tipo ? DICAS[state.tipo] ?? "" : ""));

    const ids = (lista: Array<RntrcSolicitacaoVeiculoPayload>) =>
      lista
        .map((v) => v.idRntrcSolicitacaoVeiculo)
        .filter((id): id is number => !!id);

    /** Um dono só (ou inclusão): já vem tudo marcado; com donos diferentes, o cliente escolhe. */
    const escolherServico = (tipo: TipoServicoRntrc) => {
      state.tipo = tipo;
      state.selecionados =
        grupos.value.length === 1 ? ids(grupos.value[0].veiculos) : [];
    };

    const grupoMarcado = (g: GrupoPlacas) =>
      ids(g.veiculos).every((id) => state.selecionados.indexOf(id) >= 0);

    /** No serviço em nome do dono, marcar um grupo desmarca os outros. */
    const marcarGrupo = (g: GrupoPlacas, marcar: boolean) => {
      const doGrupo = ids(g.veiculos);
      if (!marcar) {
        state.selecionados = state.selecionados.filter(
          (id) => doGrupo.indexOf(id) < 0
        );
      } else if (servicoNoNomeDoDonoAtual(state.tipo)) {
        state.selecionados = doGrupo;
      } else {
        state.selecionados = Array.from(
          new Set(state.selecionados.concat(doGrupo))
        );
      }
    };

    const dadosDaPlaca = (v: RntrcSolicitacaoVeiculoPayload) => {
      const partes = [] as Array<string>;
      if (v.flServicoVeiculoAtual) {
        partes.push(
          getServicoVeiculoLabel(v.flServicoVeiculoAtual) +
            (v.categoriaAtual ? " (" + v.categoriaAtual + ")" : "")
        );
      }
      if (v.renavamAtual) {
        partes.push("renavam " + v.renavamAtual);
      }
      if (v.propriedadeAtual) {
        partes.push(v.propriedadeAtual.toLowerCase());
      }
      if (!servicoNoNomeDoDonoAtual(state.tipo)) {
        partes.push(
          placaEmFrota(v)
            ? "na frota de " + (v.nomeTransportadorAtual ?? "")
            : "sem cadastro na ANTT"
        );
      }
      return partes.join(" · ");
    };

    const fechar = () => {
      state.aberto = false;
      emit("close");
    };

    const prosseguir = () => {
      if (!podeProsseguir.value || !props.solicitacao?.idRntrcSolicitacao) {
        return;
      }
      router.push({
        name: "rntrc-solicitacao-new",
        query: {
          tipo: state.tipo as string,
          origem: props.solicitacao.idRntrcSolicitacao + "",
          veiculos: ids(escolhidos.value).join(",")
        }
      });
    };

    onMounted(() => {
      // root: os serviços habilitados são os do cliente da consulta
      const idCliente = props.solicitacao?.cliente?.idCliente;
      const { loadTipos, loadTiposCliente } = useRntrcTipos();
      (useAuth().amIAllowed("view", "root-dashboard") && idCliente
        ? loadTiposCliente(idCliente)
        : loadTipos()
      ).then((lista) => {
        state.tiposHabilitados = lista ?? [];
        state.stateReady = true;
      });
    });

    return {
      opcoes,
      grupos,
      donosMisturados,
      podeProsseguir,
      dica,
      escolherServico,
      grupoMarcado,
      marcarGrupo,
      dadosDaPlaca,
      fechar,
      prosseguir,
      formatPlaca,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: { Checkbox },
  props: {
    solicitacao: {
      type: Object as PropType<RntrcSolicitacaoPayload>,
      required: true
    }
  }
});
